/**
 * Store Stock Checker
 *
 * Two custom elements:
 *  - <store-stock-checker>  — trigger button + variant data holder
 *  - <store-stock-drawer>   — the drawer singleton (appended to <body>)
 *
 * Flow:
 *  1. User clicks "Check store stock" → drawer opens, size picker renders
 *  2. User selects a variant → "Check availability" button enables
 *  3. User clicks "Check availability" → fetches /apps/store-stock?variant_id=XXX
 *  4. Results rendered as a list of stores with stock badges
 *
 */

if (!customElements.get('store-stock-checker')) {
  customElements.define(
    'store-stock-checker',
    class StoreStockChecker extends HTMLElement {
      connectedCallback() {
        this._variants = JSON.parse(this.querySelector('[data-store-stock-variants]')?.textContent || '[]');

        this._ensureDrawer();

        this.querySelector('.store-stock-checker__trigger-btn')?.addEventListener('click', (e) => {
          this._openDrawer(e.currentTarget);
        });
      }

      _ensureDrawer() {
        if (document.getElementById('StoreStockDrawer')) return;

        const template = document.getElementById('StoreStockDrawerTemplate');
        if (!template) return;

        const clone = template.content.cloneNode(true);
        document.body.appendChild(clone);
      }

      _openDrawer(triggerBtn) {
        const drawer = document.querySelector('store-stock-drawer');
        if (!drawer) return;

        const rawLocations = this.dataset.allowedLocations || '';
        const allowedLocations = rawLocations.length
          ? rawLocations
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
          : null;

        drawer.open({
          productTitle: this.dataset.productTitle,
          variants: this._variants,
          proxyUrl: this.dataset.proxyUrl,
          lowStockThreshold: parseInt(this.dataset.lowStockThreshold, 10) || 5,
          allowedLocations,
          triggerBtn,
        });
      }
    },
  );
}

if (!customElements.get('store-stock-drawer')) {
  customElements.define(
    'store-stock-drawer',
    class StoreStockDrawer extends HTMLElement {
      connectedCallback() {
        this._overlay = document.querySelector('.store-stock-overlay');
        this._onBodyClick = this._handleBodyClick.bind(this);

        this.querySelector('.store-stock-drawer__close')?.addEventListener('click', () => this.close());
        this._overlay?.addEventListener('click', () => this.close());

        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.close();
        });

        this.querySelector('[data-store-stock-check-btn]')?.addEventListener('click', () => {
          this._fetchStock();
        });
      }

      // ── Public API ──────────────────────────────────────────

      open({ productTitle, variants, proxyUrl, lowStockThreshold, allowedLocations, triggerBtn }) {
        this._proxyUrl = proxyUrl;
        this._lowStockThreshold = lowStockThreshold;
        this._allowedLocations = allowedLocations || null;
        this._selectedVariantId = null;
        this._triggerBtn = triggerBtn || null;

        // Reset state
        this._resetResults();
        this._setError(null);

        // Populate product name
        const nameEl = this.querySelector('[data-store-stock-product-name]');
        if (nameEl) nameEl.textContent = productTitle;

        // Build variant picker
        this._buildVariantPicker(variants);

        // Show
        this.setAttribute('open', '');
        this._overlay?.classList.add('is-visible');
        requestAnimationFrame(() => this._overlay?.classList.add('is-open'));

        document.documentElement.classList.add('store-stock-drawer--open');

        // Focus the drawer itself without stealing Dawn's global trapFocus state
        this.focus();
      }

      close() {
        this.removeAttribute('open');
        this._overlay?.classList.remove('is-open');

        const onTransitionEnd = () => {
          this._overlay?.classList.remove('is-visible');
          this._overlay?.removeEventListener('transitionend', onTransitionEnd);
        };
        this._overlay?.addEventListener('transitionend', onTransitionEnd);

        document.documentElement.classList.remove('store-stock-drawer--open');

        this._triggerBtn?.focus();
      }

      // ── Variant picker ──────────────────────────────────────

      _buildVariantPicker(variants) {
        const list = this.querySelector('[data-store-stock-variant-list]');
        if (!list) return;

        list.innerHTML = '';

        // If the product has only a default variant, skip the picker
        if (variants.length === 1 && variants[0].title === 'Default Title') {
          this._selectedVariantId = variants[0].id;
          list.closest('.store-stock-drawer__variant-list')?.previousElementSibling?.classList.add('visually-hidden');
          this._enableCheckBtn();
          return;
        }

        variants.forEach((variant) => {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'store-stock-drawer__variant-btn';
          btn.textContent = variant.title;
          btn.dataset.variantId = variant.id;

          btn.addEventListener('click', () => {
            this._selectVariant(btn, variant.id);
          });

          li.appendChild(btn);
          list.appendChild(li);
        });
      }

      _selectVariant(btn, variantId) {
        this.querySelectorAll('.store-stock-drawer__variant-btn').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this._selectedVariantId = variantId;
        this._resetResults();
        this._enableCheckBtn();
      }

      _enableCheckBtn() {
        const btn = this.querySelector('[data-store-stock-check-btn]');
        if (btn) btn.disabled = false;
      }

      // ── Fetch ───────────────────────────────────────────────

      _fetchStock() {
        if (!this._selectedVariantId || !this._proxyUrl) return;

        // Warn in development if the proxy URL looks like a direct external URL.
        // Production should always use the Shopify App Proxy path e.g. /apps/store-stock
        if (this._proxyUrl.startsWith('http') && !this._proxyUrl.includes(window.location.hostname)) {
          console.warn(
            '[store-stock-checker] Proxy URL appears to be a direct external URL. ' +
              "This will be blocked by Shopify's CSP in production. " +
              'Set the proxy URL to the App Proxy path e.g. /apps/store-stock',
          );
        }

        this._setLoading(true);
        this._resetResults();
        this._setError(null);

        const url = `${this._proxyUrl}?variant_id=${this._selectedVariantId}`;

        fetch(url)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          })
          .then((data) => {
            this._setLoading(false);
            this._renderResults(data);
          })
          .catch((err) => {
            this._setLoading(false);
            // A TypeError usually means the request was blocked (CORS / CSP / network)
            const isBlocked = err instanceof TypeError;
            this._setError(
              isBlocked
                ? 'Request was blocked. Ensure the App Proxy is configured correctly.'
                : 'Unable to load stock information. Please try again.',
            );
            console.error('[store-stock-checker] Fetch error:', err);
          });
      }

      // ── Render results ──────────────────────────────────────

      _renderResults(stores) {
        const resultsEl = this.querySelector('[data-store-stock-results]');
        const headingEl = this.querySelector('[data-store-stock-results-heading]');
        const listEl = this.querySelector('[data-store-stock-store-list]');

        if (!resultsEl || !listEl) return;

        if (!Array.isArray(stores) || stores.length === 0) {
          this._setError('No store stock information available.');
          return;
        }

        if (this._allowedLocations) {
          stores = stores.filter((s) => this._allowedLocations.includes(s.store));
        }

        if (stores.length === 0) {
          this._setError('No store stock information available.');
          return;
        }

        if (headingEl) headingEl.textContent = `Stock at ${stores.length} store${stores.length !== 1 ? 's' : ''}`;

        listEl.innerHTML = '';

        stores.forEach((store) => {
          const qty = typeof store.qty === 'number' ? store.qty : 0;
          const badgeClass = this._badgeClass(qty);
          const badgeLabel = this._badgeLabel(qty);

          const li = document.createElement('li');
          li.className = 'store-stock-drawer__store-item';
          li.innerHTML = `
            <div class="store-stock-drawer__store-info">
              <p class="store-stock-drawer__store-name">${this._escapeHtml(store.store)}</p>
              ${store.address ? `<p class="store-stock-drawer__store-address">${this._escapeHtml(store.address)}</p>` : ''}
            </div>
            <span class="badge store-stock-drawer__badge is--${badgeClass}">${badgeLabel}</span>
          `;
          listEl.appendChild(li);
        });

        resultsEl.classList.add('is-visible');
      }

      _badgeClass(qty) {
        if (qty <= 0) return 'sold-out';
        if (qty <= this._lowStockThreshold) return 'low-stock';
        return 'in-stock';
      }

      _badgeLabel(qty) {
        if (qty <= 0) return 'Out of stock';
        if (qty <= this._lowStockThreshold) return `Low stock`;
        return `In stock`;
      }

      // ── State helpers ───────────────────────────────────────

      _setLoading(visible) {
        const el = this.querySelector('[data-store-stock-loading]');
        el?.classList.toggle('is-visible', visible);
        const btn = this.querySelector('[data-store-stock-check-btn]');
        if (btn) btn.disabled = visible;
      }

      _setError(message) {
        const el = this.querySelector('[data-store-stock-error]');
        if (!el) return;
        if (message) {
          el.textContent = message;
          el.classList.add('is-visible');
        } else {
          el.textContent = '';
          el.classList.remove('is-visible');
        }
      }

      _resetResults() {
        const resultsEl = this.querySelector('[data-store-stock-results]');
        resultsEl?.classList.remove('is-visible');
        const listEl = this.querySelector('[data-store-stock-store-list]');
        if (listEl) listEl.innerHTML = '';
      }

      _handleBodyClick(evt) {
        if (!this.contains(evt.target) && !evt.target.closest('.store-stock-checker__trigger-btn')) {
          this.close();
        }
      }

      _escapeHtml(str) {
        return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      }
    },
  );
}
